import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import useDebounce from "../hooks/UseDebounce";

import InputFieldType from "../enums/InputFieldType";
import logLevel from "loglevel";
import FieldWrapper from "./FieldWrapper";

import { useFormContext } from "../hooks/useFormContext";
import EditModes from "./../enums/EditModes";
import FieldName from "./../enums/FieldName";
import UserRights from "../enums/UserRights";
import { createChangeHandler } from "./utils";
import { calculatedFields, getFieldTypes } from "./field/getFieldTypes";

const log = logLevel.getLogger("verbose");

/**
 *
 * @param {object} arg React props
 * @param {import('../dao/fields').Field} arg.data
 * @returns
 */
function FormFieldFactory({
  className,
  data,
  linkdata,
  onChange,
  onDebounce, // deferredDebounce
  required,
  formState,
  values,
  editMode,
  useUserPermissionsContext,
  entity,
  triggerFetch,
  ...props
}) {
  const [debouncedValue, setDebouncedValue] = useDebounce(undefined, 1000);
  const [errorMessage, setErrorMessage] = useState(null);
  log.trace(data.field_name, formState.values[data.field_name]);

  const { formErrors } = useFormContext();

  const { isAllowedTo, isLoading } = useUserPermissionsContext();

  const {
    field_name: fieldname,
    label: fancyName,
    type: datatype,
    values: lookupvalues,
    fieldsize = "m",
    disabled,
  } = data;

  useEffect(() => {
    // On initial render debouncedValue equals undefined and should be skipped
    if (onDebounce && debouncedValue !== undefined) {
      onDebounce({
        // calling deferred debounce with fake event object
        target: {
          name: debouncedValue.fieldname || fieldname,
          value: debouncedValue.value,
        },
      })
        .then((result) => {
          setErrorMessage(null);
          return result;
        })
        .catch((e) => {
          log.error(e);
          setErrorMessage(e.response?.body.message ?? "Unknown Error Ocurred");
        });
    }
    // REASON: Linting incorrectly forces watch on `fieldname` and `onDebounce`
  }, [debouncedValue]); // eslint-disable-line  react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof debouncedValue === "undefined" && values.object_id) {
      setDebouncedValue({
        value: values.object_id,
        fieldname: FieldName.OBJECT_ID,
      });
    }
  }, [values.object_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldSetting = {
    name: fieldname,
  };
  fieldSetting.onChange = createChangeHandler(setDebouncedValue, onChange);

  const linkedfieldSetting = {
    name: linkdata.length > 0 ? linkdata[0].fieldname : null,
    currentValue:
      linkdata.length > 0 ? formState.values[linkdata[0].fieldname] : null,
    linkActive: linkdata.length,
  };
  linkedfieldSetting.onChange = createChangeHandler(
    setDebouncedValue,
    onChange
  );

  const defaultFormProps = {
    inputProps: {
      autoComplete: "off",
    },
    // Construct className field, passing through external classes as well
    // as potentially setting conditional states as classes.
    className: clsx(
      className,
      `fieldSize--${fieldsize}`,
      !!(errorMessage || formState.errors[fieldname]) && "errorField",
      !!linkedfieldSetting.linkActive && "linkedField"
    ),
    label: fancyName,
    variant: "outlined",
    helperText: formErrors?.[fieldname],
    error: !!formErrors?.[fieldname],
    required,
    disabled,
  };

  if (required !== undefined) {
    defaultFormProps.required = required;
  }

  const isAllowedToUpdate = isAllowedTo(UserRights.UPDATE, entity);

  // Disable the fields if user is not allowed to create or update.
  if (
    editMode === EditModes.UPDATE &&
    !isLoading &&
    // eslint-disable-next-line react-hooks/rules-of-hooks
    !isAllowedToUpdate
  ) {
    defaultFormProps.disabled = true;
  }

  let actualDataType = "";
  if (datatype === "string" || datatype === "number") {
    actualDataType = lookupvalues ? InputFieldType.LOOKUP : datatype;
  } else {
    actualDataType = datatype;
  }
  const FactoryComponent = useMemo(
    () =>
      getFieldTypes(actualDataType, entity, fieldname) ||
      getFieldTypes(InputFieldType.DEFAULT, entity, fieldname),
    [actualDataType, fieldname, entity]
  );

  const fieldComponentProps = {
    values,
    data,
    fieldSetting,
    formState,
    ...(calculatedFields.includes(fieldname)
      ? {
          triggerFetch,
        }
      : {}),
    ...props,
    ...defaultFormProps,
  };

  if (linkedfieldSetting.linkActive) {
    fieldComponentProps.linkdata = linkdata;
    fieldComponentProps.linkedfieldSetting = linkedfieldSetting;
  }

  return (
    <FieldWrapper
      {...{
        data,
      }}
    >
      <FactoryComponent {...fieldComponentProps} />
    </FieldWrapper>
  );
}

export default FormFieldFactory;
